import type {
  FolderContentStatsCacheEntry,
  FolderContentStatsSlice,
} from '../../interfaces/FolderContentStatsSlice';
import type { FolderContentStats } from '../../interfaces/FileApiService';

/** Stats older than this are still shown but considered stale (refresh allowed without extra hint). */
export const FOLDER_STATS_STALE_MS = 60 * 60 * 1000;

/** Drop persisted cache entries older than this on save to localStorage. */
export const FOLDER_STATS_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** ponytail: cap in-memory + persisted entries; evict oldest by fetchedAt. */
export const FOLDER_STATS_CACHE_MAX_ENTRIES = 100;

export function isFolderContentStatsStale(fetchedAt: number, now = Date.now()): boolean {
  return now - fetchedAt > FOLDER_STATS_STALE_MS;
}

function evictOldestCacheEntries(
  cache: Record<number, FolderContentStatsCacheEntry>,
): Record<number, FolderContentStatsCacheEntry> {
  const entries = Object.entries(cache);
  if (entries.length <= FOLDER_STATS_CACHE_MAX_ENTRIES) {
    return cache;
  }

  const sortedByAge = entries.sort(([, a], [, b]) => a.fetchedAt - b.fetchedAt);
  const toRemove = sortedByAge.slice(0, entries.length - FOLDER_STATS_CACHE_MAX_ENTRIES);
  const next = { ...cache };
  for (const [fileId] of toRemove) {
    delete next[Number(fileId)];
  }
  return next;
}

export const createFolderContentStatsSlice = (
  set: (
    updater:
      | Partial<FolderContentStatsSlice>
      | ((state: FolderContentStatsSlice) => Partial<FolderContentStatsSlice>),
  ) => void,
  get: () => FolderContentStatsSlice,
): FolderContentStatsSlice => ({
  folderContentStatsCache: {},

  getFolderContentStatsFromCache: (fileId) => {
    const entry = get().folderContentStatsCache[fileId];
    return entry ?? null;
  },

  setFolderContentStatsInCache: (fileId, stats: FolderContentStats) => {
    set((state) => {
      const next: Record<number, FolderContentStatsCacheEntry> = {
        ...state.folderContentStatsCache,
        [fileId]: { fetchedAt: Date.now(), stats },
      };
      return { folderContentStatsCache: evictOldestCacheEntries(next) };
    });
  },

  resetFolderContentStatsOnLogout: () => {
    set({ folderContentStatsCache: {} });
  },
});
